<template>
    <div class="gsf">
        <div class="gsf__bar">
            <label class="gsf__search">
                <svg class="gsf__search-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M10.5 3a7.5 7.5 0 0 1 5.96 12.06l4.24 4.24a1 1 0 0 1-1.42 1.42l-4.24-4.24A7.5 7.5 0 1 1 10.5 3Zm0 2a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z"/></svg>
                <input v-model="localFilters.searchPhrase" type="search" class="gsf__search-input"
                    placeholder="Name, email or payment ID" :aria-label="$t('frontend_analytics_transactions_search')"
                    @input="searchPhraseChanged" @keydown.enter.prevent="onSearchEnter" />
                <button v-if="localFilters.searchPhrase" type="button" class="gsf__search-clear" aria-label="Clear search"
                    @click="clearSearch">
                    <cross-icon />
                </button>
            </label>

            <type-picker :options="transactionFilters" :value="localFilters.filteredTransactions"
                :preset-ids="currentTab === 'failed' ? [0] : [0, 8, 9]" @toggle="onTypeToggle" />

            <div class="gsf__date" :class="{ 'is-active': !isDefaultRange }">
                <date-range-picker ref="picker" v-model="localFilters.transactionsDateRange"
                    :opens="opens" :auto-apply="autoApply" :start-date="customStartDate"
                    :end-date="customEndDate" :ranges="customDateRanges">
                    <div slot="input" slot-scope="picker" class="gsf__date-inner">
                        <svg class="gsf__date-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm13 8H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9ZM8 13a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"/></svg>
                        <span class="gsf__date-label">{{ rangeLabel(picker.startDate, picker.endDate) }}</span>
                        <angle-down-icon class="gsf__chevron" />
                    </div>
                </date-range-picker>
            </div>

            <div class="gsf__menu-wrap gsf__menu-wrap--left" :class="{ 'is-open': sizeOpen }">
                <button ref="sizeTrigger" type="button" class="gsf__btn gsf__btn--outline" title="Rows per page"
                    aria-haspopup="listbox" :aria-expanded="sizeOpen" @click="sizeOpen = !sizeOpen">
                    {{ localFilters.selectedRowSize }}<span class="gsf__rows-unit"> rows</span>
                    <angle-down-icon class="gsf__chevron" />
                </button>
                <div v-if="sizeOpen" class="gsf__menu gsf__menu--narrow" role="listbox" aria-label="Rows per page">
                    <button v-for="opt in pageSizeOptions" :key="opt.value" type="button" class="gsf__menu-item" role="option"
                        :class="{ 'is-checked': localFilters.selectedRowSize == opt.value }"
                        :aria-selected="localFilters.selectedRowSize == opt.value" @click="setPageSize(opt.value)">
                        <span class="gsf__menu-check"><check-icon /></span>{{ opt.text }} rows
                    </button>
                </div>
            </div>

            <button type="button" class="gsf__switch" :class="{ 'is-on': localFilters.excludeFreeTransactions }"
                role="switch" :aria-checked="localFilters.excludeFreeTransactions ? 'true' : 'false'"
                aria-label="Hide free transactions"
                :title="localFilters.excludeFreeTransactions ? 'Free transactions are hidden' : 'Free transactions are shown'"
                @click="toggleExcludeFreeTransactions">
                <span class="gsf__switch-track"><span class="gsf__switch-thumb"></span></span>
                <span class="gsf__switch-label">Hide free</span>
            </button>

            <div class="gsf__apply-group">
                <button type="button" class="gsf__btn gsf__apply" :class="{ 'is-pending': hasPendingChanges }"
                    :disabled="!hasPendingChanges" aria-describedby="transaction-filter-feedback" @click="applyFilters">
                    {{ $t('apply_filters') }}
                </button>
            </div>

            <div class="gsf__actions">
            <div class="gsf__menu-wrap" :class="{ 'is-open': legendOpen }">
                <button ref="legendTrigger" type="button" class="gsf__btn gsf__btn--ghost" aria-haspopup="dialog"
                    aria-label="See legend" :aria-expanded="legendOpen" @click="legendOpen = !legendOpen">
                    <span class="gsf__swatches" aria-hidden="true">
                        <span v-for="item in legend.slice(0, 4)" :key="item.label" class="gsf__swatch-mini" :style="{ background: item.color, borderColor: item.border }"></span>
                    </span>
                    <span class="gsf__btn-text gsf__btn-text--opt">See legend</span>
                </button>
                <div v-if="legendOpen" class="gsf__menu gsf__legend" role="dialog" aria-label="Transaction legend">
                    <div class="gsf__legend-title">Row colours &amp; matching icons</div>
                    <p class="gsf__legend-intro">Row colours describe the transaction. Icons beside a name add payment or subscription details.</p>
                    <div v-for="item in legend" :key="item.label" class="gsf__legend-row">
                        <span class="gsf__swatch" :style="{ background: item.color, borderColor: item.border }" aria-hidden="true">
                            <transaction-status-icon v-if="item.indicator" :icon="item.indicator.icon" :tone="item.indicator.tone" />
                        </span>
                        <span class="gsf__legend-copy"><span class="gsf__legend-label">{{ item.label }}</span><span class="gsf__legend-hint">{{ item.hint }}</span></span>
                    </div>
                    <section class="gsf__status-guide" aria-label="Icons beside customer names">
                        <div class="gsf__legend-title">Icons beside customer names</div>
                        <p>A row can have more than one icon. Ordinary successful payments have no extra icon. A recurring-payment row does not by itself confirm an active subscription.</p>
                        <div v-for="item in statusLegend" :key="item.key" class="gsf__status-guide-row">
                            <transaction-status-icon :icon="item.icon" :tone="item.tone" />
                            <span class="gsf__legend-copy"><span class="gsf__legend-label">{{ item.title }}</span><span v-if="item.description" class="gsf__legend-hint">{{ item.description }}</span></span>
                        </div>
                    </section>
                </div>
            </div>

            <button type="button" class="gsf__btn gsf__btn--outline" @click="openAdvancedFilters">
                <filter-icon class="gsf__btn-icon" />
                <span class="gsf__btn-text">Filters</span>
                <span v-if="advancedCount" class="gsf__badge">{{ advancedCount }}</span>
            </button>

            <button type="button" class="gsf__btn gsf__btn--icon" title="Reload data with current filters"
                aria-label="Reload data with current filters" @click="hasPendingChanges ? applyFilters() : reloadData()">
                <reset-icon class="gsf__btn-icon" />
            </button>

            <div class="gsf__menu-wrap" :class="{ 'is-open': exportOpen }">
                <button ref="exportTrigger" type="button" class="gsf__btn gsf__btn--outline" aria-haspopup="menu"
                    :aria-expanded="exportOpen" :disabled="hasPendingChanges" :aria-label="$t('frontend_analytics_transactions_export')" :title="hasPendingChanges ? 'Apply your filter changes before exporting' : 'Export transactions'" @click="exportOpen = !exportOpen">
                    <download-icon class="gsf__btn-icon" />
                    <span class="gsf__btn-text gsf__btn-text--opt">{{ $t('frontend_analytics_transactions_export') }}</span>
                    <angle-down-icon class="gsf__chevron" />
                </button>
                <div v-if="exportOpen" class="gsf__menu" role="menu">
                    <button v-for="action in openPopUpActions" :key="action.text" type="button" class="gsf__menu-item"
                        role="menuitem" @click="runExport(action)">{{ action.text }}</button>
                </div>
            </div>
            </div>
        </div>

        <p id="transaction-filter-feedback" class="gsf__feedback" :class="{ 'is-pending': hasPendingChanges }" role="status" aria-live="polite">
            {{ searchPhraseTimer !== null ? 'Searching all dates…' : hasPendingChanges ? 'Filter changes not applied. Click Apply Filters to update your results.' : 'Search by name, email or payment ID. New searches include all dates and clear other filters. Apply filters to narrow the results.' }}
        </p>

        <transition name="gsf-fade">
            <div v-if="activeChips.length" class="gsf__active">
                <span class="gsf__active-label">{{ hasPendingChanges ? 'Selected filters' : 'Filtering by' }}</span>
                <button v-for="chip in activeChips" :key="chip.key" type="button" class="gsf__chip" :title="`Remove ${chip.label}`"
                    @click="removeChip(chip.key)">
                    <span class="gsf__chip-kind">{{ chip.kind }}</span>
                    <span class="gsf__chip-label">{{ chip.label }}</span>
                    <cross-icon class="gsf__chip-x" />
                </button>
                <button type="button" class="gsf__reset" @click="clearFilters">
                    {{ $t('frontend_analytics_transactions_reset') }} all
                </button>
            </div>
        </transition>

        <p v-if="currentTab === 'captured' && transactionList && transactionList.meta.totals_state === 'pending'"
            role="status" class="mt-3 text-sm text-gray-600">Calculating totals…</p>
        <p v-if="currentTab === 'captured' && transactionList && transactionList.meta.totals_state === 'error'"
            role="status" class="mt-3 text-sm text-gray-600">
            Transactions are available. Totals could not be loaded.
            <button type="button" class="underline ml-2" @click="reloadData">Retry</button>
        </p>
        <div v-if="currentTab == 'captured'" class="gsf__stats">
            <div v-for="stat in stats" :key="stat.title" class="gsf__stat" :class="{ 'gsf__stat--accent': stat.accent }">
                <span class="gsf__stat-value">{{ stat.value }}</span>
                <span class="gsf__stat-title">{{ stat.title }}</span>
            </div>
        </div>
    </div>
</template>
<script>
import moment from 'moment';
import ResetIcon from '@/assets/images/icons/ResetIcon.svg';
import FilterIcon from '@/assets/images/icons/FilterIcon.svg';
import CrossIcon from '@/assets/images/icons/CrossIcon.svg';
import DownloadIcon from '@/assets/images/icons/DownloadIcon.svg';
import AngleDownIcon from '@/assets/images/icons/AngleDownIcon.svg';
import CheckIcon from '@/assets/images/icons/CheckIcon.svg';
import TypePicker from './TypePicker';
import TransactionStatusIcon from '@/renderers/transactionStatusIcon.vue';
import { statusIndicators } from '@/helpers/transactionTableStatus';

export default {
    components: {
        TransactionStatusIcon,
        ResetIcon,
        FilterIcon,
        CrossIcon,
        DownloadIcon,
        AngleDownIcon,
        CheckIcon,
        TypePicker,
    },
    props: {
        filteredFunnels: {
            type: Array,
        },
        filteredAffiliates: {
            type: Array,
        },
        filteredSecondTierAffiliates: {
            type: Array,
        },
        filteredAdvancedTransactions: {},
        currentTab: {
            type: String,
        },
        filteredTransactions: {
            type: Array,
        },
        transactionsDateRange: {
            type: Object,
        },
        reloadData: {
            type: Function,
        },
        openPopUp: {
            type: Function,
        },
        transactionList: {
            required: true,

        },
    },
    data() {
        return {
            appliedFilterKey: null,
            appliedSearchPhrase: '',
            searchPhraseTimer: null,
            exportOpen: false,
            sizeOpen: false,
            legendOpen: false,
            localFilters: {
                filteredTransactions: [...(this.filteredTransactions || [0])],
                transactionsDateRange: { ...this.transactionsDateRange },
                selectedRowSize: 25,
                searchPhrase: '',
                excludeFreeTransactions: false,
            },
            opens: 'center',
            autoApply: true,
            customStartDate: moment(),
            customEndDate: moment(),
            customDateRanges: {
                Today: [moment(), moment()],
                Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                'This Week': [moment().startOf('week'), moment().endOf('week')],
                'Last Week': [
                    moment().subtract(1, 'week').startOf('week'),
                    moment().subtract(1, 'week').endOf('week'),
                ],
                'This Month': [moment().startOf('month'), moment().endOf('month')],
                'Last Month': [
                    moment().subtract(1, 'month').startOf('month'),
                    moment().subtract(1, 'month').endOf('month'),
                ],
                'This Year': [moment().startOf('year'), moment().endOf('year')],
                'Last Year': [
                    moment().subtract(1, 'year').startOf('year'),
                    moment().subtract(1, 'year').endOf('year'),
                ],
                'All Dates': [moment('20191201', 'YYYYMMDD'), moment()],
            },

        }
    },
    mounted() {
        this.markFiltersApplied();
        document.addEventListener('mousedown', this.onDocumentDown);
        document.addEventListener('keydown', this.onKey);
    },
    beforeDestroy() {
        this.clearSearchPhraseTimer();
        document.removeEventListener('mousedown', this.onDocumentDown);
        document.removeEventListener('keydown', this.onKey);
    },
    computed: {
        filterKey() {
            const f = this.localFilters;
            const ids = items => (items || []).map(item => String(item.id === undefined ? item : item.id)).sort();
            return JSON.stringify({
                search: f.searchPhrase, types: ids(f.filteredTransactions), rows: Number(f.selectedRowSize),
                from: moment(f.transactionsDateRange.startDate).format('YYYY-MM-DD'),
                to: moment(f.transactionsDateRange.endDate).format('YYYY-MM-DD'), free: !!f.excludeFreeTransactions,
                funnels: ids(this.filteredFunnels), affiliates: ids(this.filteredAffiliates),
                secondTier: ids(this.filteredSecondTierAffiliates), advanced: this.filteredAdvancedTransactions,
            });
        },
        hasPendingChanges() { return this.appliedFilterKey !== null && this.filterKey !== this.appliedFilterKey; },
        isDefaultRange() {
            return this.isDefaultDateRange(this.localFilters.transactionsDateRange.startDate) &&
                this.isDefaultDateRange(this.localFilters.transactionsDateRange.endDate);
        },
        isAllAffiliates() {
            return this.filteredAffiliates?.length == 1 &&
                this.filteredAffiliates[0].id == 0 &&
                this.filteredAffiliates[0].name === 'All';
        },
        isAllSecondTier() {
            return this.filteredSecondTierAffiliates?.length == 1 &&
                this.filteredSecondTierAffiliates[0].id == 0 &&
                this.filteredSecondTierAffiliates[0].name === 'All';
        },
        isDefaultType() {
            return this.localFilters.filteredTransactions?.length == 1 && this.localFilters.filteredTransactions[0] == 0;
        },
        // Number of filters set inside the Advanced Filters modal (shown as a badge)
        advancedCount() {
            let n = 0;
            if (this.filteredFunnels?.length > 0) n += 1;
            if (!this.isAllAffiliates) n += 1;
            if (this.currentTab !== 'failed' && !this.isAllSecondTier) n += 1;
            if (this.currentTab !== 'failed' && this.filteredAdvancedTransactions != 0) n += 1;
            return n;
        },
        // Mirrors the row classes styled in assets/scss/analytics-transactions.scss
        statusLegend() {
            return Object.entries(statusIndicators).map(([key, item]) => {
                const [title, description] = item.label.split(' — ');
                return { key, ...item, title, description };
            });
        },
        legend() {
            const rows = [
                { label: this.$t('frontend_analytics_transactions_sale'), hint: 'One-time purchase', color: '#ffffff', border: '#cbd5e1' },
                { label: this.$t('frontend_analytics_transactions_initial_payment'), hint: 'First charge of a subscription', color: '#f2f3ff', border: '#b4b9f5' },
                { label: this.$t('frontend_analytics_transactions_initial_rebill'), hint: 'Recurring payment', color: '#edfff8', border: '#8fdcb9' },
                { label: this.$t('frontend_analytics_transactions_free_transactions'), hint: 'No charge', color: '#eff8ff', border: '#9ccdf3', capturedOnly: true },
                { label: this.$t('frontend_analytics_transactions_cancellations'), hint: 'Cancellation record', indicator: statusIndicators['cancelled-record'], color: '#fffdea', border: '#e6d66a' },
                { label: this.$t('frontend_analytics_transactions_refund'), hint: 'Money returned', indicator: statusIndicators['refund-record'], color: '#fff3f7', border: '#f5a3c4' },
                { label: this.$t('frontend_analytics_transactions_suspected_fraud'), hint: 'Affiliate flagged for review', indicator: statusIndicators.flagged, color: '#fff7e8', border: '#f0c470' },
            ];
            return this.currentTab === 'failed' ? rows.filter((r) => !r.capturedOnly) : rows;
        },
        transactionFilters() {
            const TransactionFilters = [
                { id: 0, label: this.$t('frontend_analytics_transactions_all_transactions') },
                { id: 8, label: this.$t('frontend_analytics_transactions_only_the_good') },
                { id: 9, label: this.$t('frontend_analytics_transactions_only_the_bad') },
                { id: 1, label: this.$t('frontend_analytics_transactions_sale') },
                { id: 3, label: this.$t('frontend_analytics_transactions_initial_payment') },
                { id: 2, label: this.$t('frontend_analytics_transactions_initial_rebill') },
                { id: 7, label: this.$t('frontend_analytics_transactions_free_transactions') },
                { id: 4, label: this.$t('frontend_analytics_transactions_cancellations') },
                { id: 5, label: this.$t('frontend_analytics_transactions_refund') },
                { id: 6, label: this.$t('frontend_analytics_transactions_suspected_fraud') },
            ];
            const failedTransactionFilters = [
                { id: 0, label: this.$t('frontend_analytics_all_failed_rebills') },
                { id: 1, label: this.$t('frontend_analytics_successfully_captured') },
                { id: 2, label: this.$t('frontend_analytics_pending_dunning') },
                { id: 3, label: this.$t('frontend_analytics_unable_to_recover') },
            ];
            return this.currentTab === 'failed' ? failedTransactionFilters : TransactionFilters;
        },
        pageSizeOptions() {
            return [
                { text: '5', value: 5 },
                { text: '10', value: 10 },
                { text: '25', value: 25 },
                { text: '50', value: 50 },
                { text: '100', value: 100 }
            ];
        },
        openPopUpActions() {
            return [
                {
                    text: this.$t('frontend_analytics_transactions_export_all'),
                    callback: () => this.openPopUp(true),
                },
                {
                    text: this.$t('frontend_analytics_transactions_export_this_page'),
                    callback: () => this.openPopUp(),
                },
            ];
        },
        advancedTransactionLabel() {
            const labels = {
                1: this.$t('frontend_analytics_first_sale_or'),
                2: this.$t('frontend_analytics_first_sale_after_trail'),
                3: this.$t('frontend_analytics_rebills_only'),
            };
            return labels[this.filteredAdvancedTransactions] || '';
        },
        activeChips() {
            const chips = [];
            const f = this.localFilters;
            if (f.searchPhrase) chips.push({ key: 'search', kind: 'Search', label: `"${f.searchPhrase}"` });
            if (!this.isDefaultType) {
                const labels = (f.filteredTransactions || [])
                    .map((id) => this.transactionFilters.find((o) => o.id == id)?.label)
                    .filter(Boolean);
                chips.push({ key: 'type', kind: 'Type', label: labels.join(', ') });
            }
            if (!this.isDefaultRange) {
                chips.push({ key: 'date', kind: 'Dates', label: this.rangeLabel(f.transactionsDateRange.startDate, f.transactionsDateRange.endDate) });
            }
            if (f.selectedRowSize != 25) chips.push({ key: 'size', kind: 'Rows', label: `${f.selectedRowSize} per page` });
            if (f.excludeFreeTransactions) chips.push({ key: 'free', kind: 'Free', label: 'Hidden' });
            if (this.filteredFunnels?.length > 0) {
                const n = this.filteredFunnels.length;
                chips.push({ key: 'funnels', kind: 'Funnels', label: `${n} ${n === 1 ? 'selection' : 'selections'}` });
            }
            if (!this.isAllAffiliates) chips.push({ key: 'affiliates', kind: 'Affiliates', label: this.affiliateLabel(this.filteredAffiliates) });
            if (this.currentTab !== 'failed' && !this.isAllSecondTier) {
                chips.push({ key: 'second', kind: '2nd tier', label: this.affiliateLabel(this.filteredSecondTierAffiliates) });
            }
            if (this.currentTab !== 'failed' && this.filteredAdvancedTransactions != 0) {
                chips.push({ key: 'advanced', kind: 'Sale type', label: this.advancedTransactionLabel });
            }
            return chips;
        },
        stats() {
            const meta = this.transactionList?.meta || {};
            const currency = value => {
                if (!this.transactionList || meta.totals_state === 'pending' || meta.totals_state === 'deferred') return '…';
                if (meta.totals_state === 'error') return '—';
                return this.$options.filters.currency(value);
            };
            const numFormat = this.$options.filters.numFormat;
            return [
                { title: this.$t('frontend_analytics_transactions_transactions'), value: this.transactionList ? numFormat(meta.total) : '…', accent: true },
                { title: this.$t('frontend_analytics_transactions_revenue'), value: currency(meta.total_revenue), accent: true },
                { title: this.$t('frontend_analytics_transactions_refunded'), value: currency(meta.total_refunded) },
                { title: this.$t('frontend_analytics_transactions_processing_fee'), value: currency(meta.total_processing_fee) },
                { title: this.$t('frontend_analytics_transactions_commisions'), value: currency(meta.total_commission) },
                { title: this.$t('frontend_analytics_transactions_commisions_second_tier'), value: currency(meta.total_commission_second_tier) },
                { title: this.$t('frontend_analytics_transactions_vendor_revenue'), value: currency(meta.total_vendor_revenue), accent: true },
            ];
        },
    },
    methods: {
        isDefaultDateRange(dateString) {
            return moment(dateString).format('YYYY-MM-DD') == moment().format('YYYY-MM-DD');
        },
        rangeLabel(start, end) {
            const s = moment(start);
            const e = moment(end);
            if (s.isSame(e, 'day')) {
                return s.isSame(moment(), 'day') ? `Today, ${s.format('MMM D')}` : s.format('MMM D, YYYY');
            }
            if (s.isSame(e, 'year')) return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`;
            return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
        },
        affiliateLabel(list) {
            if (!list || list.length === 0) return 'Specific';
            const first = list[0];
            if (first.id == -2) return 'Only affiliates';
            if (first.id == -1) return 'Only non-affiliates';
            if (list.length === 1) return first.name;
            return `${first.name} +${list.length - 1}`;
        },
        onDocumentDown(e) {
            const inside = Array.from(this.$el.querySelectorAll('.gsf__menu-wrap')).some((w) => w.contains(e.target));
            if (!inside) {
                this.exportOpen = false;
                this.sizeOpen = false;
                this.legendOpen = false;
            }
        },
        onKey(e) {
            if (e.key !== 'Escape') return;
            if (this.exportOpen) {
                this.exportOpen = false;
                this.$refs.exportTrigger?.focus();
            }
            if (this.sizeOpen) {
                this.sizeOpen = false;
                this.$refs.sizeTrigger?.focus();
            }
            if (this.legendOpen) {
                this.legendOpen = false;
                this.$refs.legendTrigger?.focus();
            }
        },
        setPageSize(value) {
            this.sizeOpen = false;
            if (this.localFilters.selectedRowSize == value) return;
            this.localFilters.selectedRowSize = value;
        },
        runExport(action) {
            this.exportOpen = false;
            action.callback();
        },
        // Safe in browser and in Node evalmachine tests (no bare timer globals there).
        clearSearchPhraseTimer() {
            if (this.searchPhraseTimer == null) return;
            if (typeof clearTimeout === 'function') clearTimeout(this.searchPhraseTimer);
            this.searchPhraseTimer = null;
        },
        normalizedSearchPhrase() {
            return (this.localFilters.searchPhrase || '').trim().replace(/\s+/g, ' ');
        },
        // Every search uses the same submission path, including names and partial IDs.
        searchPhraseChanged() {
            this.clearSearchPhraseTimer();
            this.$emit('search-pending');
            if (!this.normalizedSearchPhrase()) {
                this.clearSearch();
                return;
            }
            const applySearch = () => {
                this.searchPhraseTimer = null;
                this.applyFilters();
            };
            if (typeof setTimeout === 'function') {
                this.searchPhraseTimer = setTimeout(applySearch, 600);
            } else {
                applySearch();
            }
        },
        onSearchEnter() {
            this.applyFilters();
        },
        clearSearch() {
            this.clearSearchPhraseTimer();
            this.localFilters.searchPhrase = '';
            this.applyFilters();
        },
        toggleExcludeFreeTransactions() {
            this.localFilters.excludeFreeTransactions = !this.localFilters.excludeFreeTransactions;
        },
        // The picker reports a click; append or remove the id so the "last added" rules below still apply
        onTypeToggle(id) {
            const current = this.localFilters.filteredTransactions || [];
            if (current.some((v) => v == id)) {
                this.localFilters.filteredTransactions = current.filter((v) => v != id);
            } else {
                this.localFilters.filteredTransactions = [...current, id];
            }
            this.onTransactionFilterChange();
        },
        onTransactionFilterChange() {
            if (this.currentTab === 'failed') {
                this.$nextTick(() => {
                    if (this.localFilters.filteredTransactions && this.localFilters.filteredTransactions.length > 0) {
                        const lastAddedTransactionFilterId =
                            this.localFilters.filteredTransactions[this.localFilters.filteredTransactions.length - 1];

                        if (lastAddedTransactionFilterId == 0) {
                            this.localFilters.filteredTransactions = this.localFilters.filteredTransactions.slice(-1);
                        } else {
                            this.localFilters.filteredTransactions = this.localFilters.filteredTransactions.filter((obj) => { return obj !== 0; });
                        }
                    } else {
                        this.localFilters.filteredTransactions = [0];
                    }
                });
            } else {
                this.$nextTick(() => {
                    if (this.localFilters.filteredTransactions && this.localFilters.filteredTransactions.length > 0) {
                        const lastAddedTransactionFilterId = this.localFilters.filteredTransactions[this.localFilters.filteredTransactions.length - 1];
                        if (lastAddedTransactionFilterId == 0) {
                            this.localFilters.filteredTransactions = this.localFilters.filteredTransactions.slice(-1);
                        }
                        else if (lastAddedTransactionFilterId == 8) {
                            this.localFilters.filteredTransactions = [1, 2, 3, 6];
                        }
                        else if (lastAddedTransactionFilterId == 9) {
                            this.localFilters.filteredTransactions = [4, 5];
                        }
                        else {
                            this.localFilters.filteredTransactions = this.localFilters.filteredTransactions.filter((obj) => { return obj !== 0; });
                        }
                    } else {
                        // Unticking the last type means "everything" again
                        this.localFilters.filteredTransactions = [0];
                    }
                });
            }

        },
        openAdvancedFilters() {
            const modalName = this.currentTab === 'failed' ? 'advanced-failed-filters-modal' : 'advanced-filters-modal';
            this.$root.$emit('modal-open', modalName);
        },
        // Removing a chip stages the change just like its corresponding control.
        removeChip(key) {
            switch (key) {
                case 'search':
                    this.clearSearch();
                    return;
                case 'type':
                    this.localFilters.filteredTransactions = [0];

                    break;
                case 'date':
                    this.localFilters.transactionsDateRange = { startDate: moment(), endDate: moment() };

                    break;
                case 'size':
                    this.localFilters.selectedRowSize = 25;
                    break;
                case 'free':
                    this.toggleExcludeFreeTransactions();
                    return;
                case 'funnels':
                    this.$emit('filter-changed', { key: 'filteredFunnels', value: [] });
                    break;
                case 'affiliates':
                    this.$emit('filter-changed', { key: 'filteredAffiliates', value: [{ id: 0, name: 'All' }] });
                    break;
                case 'second':
                    this.$emit('filter-changed', { key: 'filteredSecondTierAffiliates', value: [{ id: 0, name: 'All' }] });
                    break;
                case 'advanced':
                    this.$emit('filter-changed', { key: 'filteredAdvancedTransactions', value: 0 });
                    break;
            }
        },
        clearFilters({ searchPhrase = '', allTime = false } = {}) {
            this.clearSearchPhraseTimer();
            this.localFilters.filteredTransactions = [0];
            this.localFilters.searchPhrase = searchPhrase;
            this.localFilters.selectedRowSize = 25;
            this.localFilters.transactionsDateRange = {
                startDate: allTime ? moment('20191201', 'YYYYMMDD') : moment(),
                endDate: moment(),
            };
            this.localFilters.excludeFreeTransactions = false;

            this.$emit('filter-changed', {
                key: 'clear',
                values: [
                    { key: 'selectedRowSize', value: 25 },
                    { key: 'transactionsDateRange', value: this.localFilters.transactionsDateRange},
                    { key: 'excludeFreeTransactions', value: false},
                    { key: 'filteredTransactions', value: [0] },
                    { key: 'filteredFailedTransactions', value: [0] },
                    { key: 'searchPhrase', value: this.localFilters.searchPhrase },
                    { key: 'filteredAffiliates', value: [{ id: 0, name: 'All' }] },
                    { key: 'filteredSecondTierAffiliates', value: [{ id: 0, name: 'All' }] },
                    { key: 'filteredFunnels', value: [] },
                    { key: 'filteredAdvancedTransactions', value: 0 }
                ]
            });
            this.$emit('apply-filters');
            this.$nextTick(this.markFiltersApplied);
        },
        markFiltersApplied() {
            this.appliedFilterKey = this.filterKey;
            this.appliedSearchPhrase = this.normalizedSearchPhrase();
        },
        applyFilters() {
            this.clearSearchPhraseTimer();
            const phrase = this.normalizedSearchPhrase();
            this.localFilters.searchPhrase = phrase;
            if (phrase && phrase !== this.appliedSearchPhrase) {
                this.clearFilters({ searchPhrase: phrase, allTime: true });
                return;
            }
            // Commit the whole draft before the parent requests any rows.
            Object.entries(this.localFilters).forEach(([key, value]) => {
                const parentKey = key === 'filteredTransactions' && this.currentTab === 'failed' ? 'filteredFailedTransactions' : key;
                this.$emit('filter-changed', { key: parentKey, value });
            });
            this.$emit('apply-filters');
            this.$nextTick(this.markFiltersApplied);
        },
    },
    watch: {
        currentTab() {
            this.clearSearchPhraseTimer();
            this.localFilters.filteredTransactions = [...(this.filteredTransactions || [0])];
        },
        transactionsDateRange: {
            handler() {
                this.localFilters.transactionsDateRange = { ...this.transactionsDateRange };
            },
            deep: true
        },
        filteredTransactions: {
            handler() {
                this.localFilters.filteredTransactions = [...(this.filteredTransactions || [0])];
            },
            deep: true
        },
    }
}
</script>

<style lang="scss">
.gsf {
    --gsf-primary: #f22987;
    --gsf-primary-dark: #e01d7a;
    --gsf-primary-soft: #fff0f7;
    --gsf-primary-ring: rgba(242, 41, 135, 0.22);
    --gsf-fg: #182230;
    --gsf-muted: #667085;
    --gsf-faint: #98a2b3;
    --gsf-border: #e2e8f0;
    --gsf-border-strong: #cbd5e1;
    --gsf-surface: #f8fafc;
    --gsf-surface-2: #f1f5f9;
    --gsf-radius: 10px;

    margin-top: 14px;
    color: var(--gsf-fg);

    button { font: inherit; }

    // ---------- main bar ----------
    &__bar {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 10px;
        padding: 14px 16px;
        background: #fff;
        border: 1px solid var(--gsf-border);
        border-radius: 14px;
        box-shadow: 0 1px 2px rgba(24, 34, 48, 0.04), 0 12px 32px -16px rgba(24, 34, 48, 0.12);
    }

    &__spacer { flex: 1 1 8px; }
    &__apply-group { flex: 0 0 auto; margin-left: auto; }
    &__switch, &__menu-wrap { flex: 0 0 auto; }
    .tpk { flex: 0 0 auto; }
    &__btn.gsf__apply {
        min-width: 132px; background: #eef1f5; border-color: #dce2ea; color: #667085;
        &:disabled { opacity: 1; }
        &.is-pending {
            background: var(--gsf-primary); border-color: var(--gsf-primary); color: #fff;
            box-shadow: 0 4px 12px rgba(242, 41, 135, .2);
            animation: gsf-apply-nudge 2s ease-out infinite;
            &:hover { background: var(--gsf-primary-dark); }
        }
    }
    &__feedback { margin: 10px 16px 0; font-size: 12px; color: var(--gsf-muted); line-height: 1.5; }
    &__feedback.is-pending { color: #ac225f; font-weight: 600; }

    // Right-hand cluster stays right-aligned and may shrink rather than push
    // the bar wider than its container; its labels elide instead of overflowing.
    &__actions {
        display: flex;
        flex: 0 1 auto;
        min-width: 0;
        align-items: center;
        gap: 10px;
        margin-left: auto;

        .gsf__menu-wrap { display: flex; flex: 0 1 auto; min-width: 0; }
        .gsf__btn { max-width: 100%; }
    }

    &__search {
        position: relative;
        display: flex;
        align-items: center;
        flex: 1 1 120px;
        min-width: 0;
        max-width: 420px;
        height: 42px;
        padding: 0 10px 0 40px;
        border: 1px solid var(--gsf-border);
        border-radius: var(--gsf-radius);
        background: #fff;
        transition: border-color 120ms ease, box-shadow 120ms ease;

        &:focus-within { border-color: var(--gsf-primary); box-shadow: 0 0 0 3px var(--gsf-primary-ring); }
    }

    &__search-icon { position: absolute; left: 14px; width: 15px; height: 15px; fill: var(--gsf-faint); pointer-events: none; }

    &__search-input {
        flex: 1 1 auto;
        min-width: 0;
        height: 100%;
        border: 0;
        outline: none;
        background: transparent;
        font: inherit;
        font-size: 14px;
        color: var(--gsf-fg);

        &::placeholder { color: var(--gsf-faint); }
        &::-webkit-search-cancel-button { display: none; }
    }

    &__search-clear {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: var(--gsf-faint);
        cursor: pointer;

        svg { width: 13px; height: 13px; fill: currentColor; }
        &:hover { background: var(--gsf-surface-2); color: var(--gsf-fg); }
    }

    &__chevron { flex: 0 0 auto; width: 12px; height: 12px; fill: currentColor; opacity: 0.7; }

    // Date range: re-skin the vue2-daterange-picker trigger
    &__date {
        flex: 0 1 auto;
        min-width: 0;
        max-width: 260px;

        .vue-daterange-picker { display: block; min-width: 0; max-width: 100%; }

        .reportrange-text {
            display: block !important;
            height: 42px;
            padding: 0 !important;
            border: 1px solid var(--gsf-border) !important;
            border-radius: var(--gsf-radius) !important;
            background: #fff !important;
            box-shadow: none !important;
            font: inherit;
            cursor: pointer;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
            transition: border-color 120ms ease, box-shadow 120ms ease;

            &:hover { border-color: var(--gsf-border-strong) !important; }
        }

        &.is-active .reportrange-text {
            border-color: var(--gsf-primary) !important;
            background: var(--gsf-primary-soft) !important;
            color: var(--gsf-primary);
        }

        .daterangepicker {
            width: 680px; max-width: calc(100vw - 32px); padding: 12px;
            border-radius: 16px; border-color: var(--gsf-border); color: var(--gsf-fg);
            box-shadow: 0 18px 48px -12px rgba(24, 34, 48, .24); margin-top: 10px;
            &::before, &::after { display: none; }
            .ranges {
                width: 146px; margin: 0; padding: 4px 12px 4px 0;
                ul { width: auto; }
                li { margin: 2px 0; padding: 9px 12px; border-radius: 8px; font-size: 12px; font-weight: 500; color: #526078; }
                li:hover { background: #f1f5f9; color: var(--gsf-fg); }
                li.active { background: var(--gsf-primary-soft); color: #b91e65; font-weight: 700; }
            }
            .drp-calendar { width: 248px; max-width: none; padding: 4px 8px; }
            .drp-calendar.left { border-left: 1px solid #edf0f5; }
            .calendar-table { border: 0; }
            .calendar-table td, .calendar-table th { min-width: 30px; width: 30px; height: 32px; line-height: 32px; font-size: 12px; }
            .calendar-table th { color: #768297; font-weight: 500; }
            .calendar-table th.month { color: #26334a; font-size: 14px; font-weight: 700; padding-bottom: 8px; }
            td.available:hover { background: #f1f5f9; color: #26334a; }
            td.in-range { background: #fff0f7; color: #9c2159; }
            td.active, td.active:hover { background: var(--gsf-primary); color: #fff; font-weight: 700; }
            td.start-date { border-radius: 8px 0 0 8px; }
            td.end-date { border-radius: 0 8px 8px 0; }
            td.start-date.end-date { border-radius: 8px; }
            td.off { color: #a5aebb; background: #fff; }
            .calendar-table .next span, .calendar-table .prev span { border-color: #667085; border-width: 0 1.5px 1.5px 0; padding: 3px; }
        }
        @media (max-width: 1000px) {
            .daterangepicker { position: fixed; left: 50% !important; right: auto !important; top: 22vh !important; transform: translateX(-50%); max-height: 72vh; overflow-y: auto; }
        }
        @media (max-width: 720px) {
            .daterangepicker {
                width: min(360px, calc(100vw - 24px)); top: 10vh !important; max-height: 80vh;
                .ranges { float: none; width: 100%; padding: 0 0 10px; border-bottom: 1px solid #edf0f5; }
                .ranges ul { display: flex; flex-wrap: wrap; gap: 4px; }
                .ranges li { flex: 1 0 28%; text-align: center; padding: 8px 4px; font-size: 11px; }
                .drp-calendar, .drp-calendar.left, .drp-calendar.right { width: 100%; float: none; border: 0; padding: 10px 0 0; }
                .calendar-table td, .calendar-table th { height: 36px; line-height: 36px; }
            }
        }
    }

    &__date-inner {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 100%;
        min-width: 0;
        padding: 0 12px 0 12px;
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
    }

    &__date-label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__date-icon { flex: 0 0 auto; width: 15px; height: 15px; fill: currentColor; opacity: 0.75; }

    // Hide-free switch
    &__switch {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 42px;
        padding: 0 12px;
        border: 1px solid var(--gsf-border);
        border-radius: var(--gsf-radius);
        background: #fff;
        font-size: 13.5px;
        font-weight: 600;
        color: var(--gsf-muted);
        cursor: pointer;
        white-space: nowrap;
        transition: border-color 120ms ease, color 120ms ease, background 120ms ease;

        &:hover { border-color: var(--gsf-border-strong); color: var(--gsf-fg); }
        &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--gsf-primary-ring); }

        &.is-on {
            border-color: var(--gsf-primary);
            background: var(--gsf-primary-soft);
            color: var(--gsf-primary);
        }
    }

    &__switch-track {
        position: relative;
        width: 30px;
        height: 18px;
        border-radius: 999px;
        background: var(--gsf-border-strong);
        transition: background 140ms ease;
    }

    &__switch-thumb {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 14px;
        height: 14px;
        border-radius: 999px;
        background: #fff;
        box-shadow: 0 1px 2px rgba(24, 34, 48, 0.25);
        transition: transform 140ms ease;
    }

    &__switch.is-on &__switch-track { background: var(--gsf-primary); }
    &__switch.is-on &__switch-thumb { transform: translateX(12px); }

    // Buttons
    &__btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        height: 42px;
        padding: 0 14px;
        border: 1px solid transparent;
        border-radius: var(--gsf-radius);
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
        min-width: 0;
        cursor: pointer;
        transition: background 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease;

        &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--gsf-primary-ring); }
        &:disabled { cursor: default; opacity: 0.45; }

        &--outline {
            background: #fff;
            border-color: var(--gsf-border);
            color: var(--gsf-fg);
            &:hover:not(:disabled) { border-color: var(--gsf-border-strong); background: var(--gsf-surface); }
        }

        &--icon {
            width: 42px;
            padding: 0;
            background: #fff;
            border-color: var(--gsf-border);
            color: var(--gsf-muted);
            &:hover:not(:disabled) { border-color: var(--gsf-border-strong); color: var(--gsf-fg); .gsf__btn-icon { transform: rotate(-40deg); } }
        }

        &--primary {
            padding: 0 18px;
            background: var(--gsf-primary);
            border-color: var(--gsf-primary);
            color: #fff;
            box-shadow: 0 8px 20px -6px rgba(242, 41, 135, 0.45);
            &:hover:not(:disabled) { background: var(--gsf-primary-dark); border-color: var(--gsf-primary-dark); transform: translateY(-1px); }
            &:disabled { box-shadow: none; }
        }

        &--sm { height: 36px; padding: 0 16px; font-size: 13.5px; }

        &--ghost {
            padding: 0 10px;
            background: transparent;
            color: var(--gsf-muted);
            &:hover:not(:disabled) { background: var(--gsf-surface-2); color: var(--gsf-fg); }
        }
    }

    &__btn-icon { width: 14px; height: 14px; fill: currentColor; transition: transform 200ms ease; }

    // Button labels stay on one line but may elide so the bar never overflows.
    &__btn-text { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

    // Legend
    &__swatches { display: inline-flex; }
    &__swatch-mini {
        width: 14px;
        height: 14px;
        border-radius: 4px;
        border: 1px solid;
        margin-left: -5px;
        box-shadow: 0 0 0 1.5px #fff;
        &:first-child { margin-left: 0; }
    }

    &__menu.gsf__legend { width: min(420px, calc(100vw - 32px)); min-width: 0; max-height: min(560px, 50dvh); overflow: auto; overscroll-behavior: contain; padding: 16px; white-space: normal; }
    &__legend-intro, &__status-guide p { margin: 0 0 12px; color: var(--gsf-faint); font-size: 12px; line-height: 1.5; }
    &__status-guide { padding-top: 16px; margin-top: 12px; border-top: 1px solid var(--gsf-border); }
    &__status-guide-row { display: grid; grid-template-columns: 32px minmax(0, 1fr); align-items: start; gap: 12px; padding: 8px 0;
        > svg { margin: 3px auto 0; flex-shrink: 0; }
    }
    &__legend-title { margin-bottom: 8px; font-size: 13px; font-weight: 700; color: var(--gsf-fg); }
    &__legend-row { display: grid; grid-template-columns: 32px minmax(0, 1fr); align-items: center; gap: 12px; padding: 7px 0; font-size: 13px; }
    &__swatch { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 30px; border-radius: 7px; border: 1px solid; }
    &__legend-copy { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    &__legend-label { font-size: 13px; font-weight: 600; color: var(--gsf-fg); line-height: 1.4; }
    &__legend-hint { color: var(--gsf-faint); font-size: 12px; line-height: 1.5; }

    @media (max-width: 600px) {
        &__menu.gsf__legend { position: fixed; top: 80px; bottom: auto; left: 16px; right: 16px; width: auto; max-height: calc(100dvh - 96px); }
    }

    &__badge {
        min-width: 20px;
        padding: 1px 6px;
        border-radius: 999px;
        background: var(--gsf-primary);
        color: #fff;
        font-size: 11.5px;
        font-weight: 700;
        text-align: center;
    }

    // Export menu
    &__menu-wrap { position: relative; }
    &__menu-wrap.is-open > .gsf__btn { border-color: var(--gsf-primary); box-shadow: 0 0 0 3px var(--gsf-primary-ring); }

    &__menu {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        z-index: 40;
        min-width: 190px;
        padding: 6px;
        border: 1px solid var(--gsf-border);
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 16px 40px -12px rgba(24, 34, 48, 0.28), 0 0 0 1px rgba(24, 34, 48, 0.03);
    }

    &__menu-wrap--left .gsf__menu { left: 0; right: auto; }
    &__menu--narrow { min-width: 150px; }

    &__menu-check {
        display: inline-flex;
        width: 14px;
        margin-right: 8px;
        color: var(--gsf-primary);
        vertical-align: middle;
        svg { width: 12px; height: 12px; fill: currentColor; opacity: 0; }
    }

    &__menu-item.is-checked { font-weight: 600; .gsf__menu-check svg { opacity: 1; } }

    &__menu-item {
        display: block;
        width: 100%;
        padding: 9px 12px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        text-align: left;
        font-size: 13.5px;
        font-weight: 500;
        color: var(--gsf-fg);
        cursor: pointer;

        &:hover { background: var(--gsf-surface); }
        &:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--gsf-primary-ring); }
    }

    // ---------- active filter chips ----------
    &__active {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        padding: 10px 16px 0;
    }

    &__active-label { font-size: 12.5px; font-weight: 600; color: var(--gsf-faint); margin-right: 2px; }

    &__chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        max-width: 100%;
        padding: 4px 8px 4px 10px;
        border-radius: 999px;
        border: 1px solid #fbcfe3;
        background: var(--gsf-primary-soft);
        font-size: 12.5px;
        color: var(--gsf-fg);
        cursor: pointer;
        transition: border-color 100ms ease;

        &:hover { border-color: var(--gsf-primary); .gsf__chip-x { color: var(--gsf-primary); } }
        &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--gsf-primary-ring); }
    }

    &__chip-kind { color: var(--gsf-muted); &::after { content: ':'; } }
    &__chip-label { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 260px; }
    &__chip-x { width: 11px; height: 11px; fill: currentColor; color: var(--gsf-faint); }

    &__reset {
        margin-left: 4px;
        padding: 4px 8px;
        border: 0;
        background: transparent;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--gsf-muted);
        text-decoration: underline;
        text-underline-offset: 2px;
        cursor: pointer;

        &:hover { color: var(--gsf-primary); }
    }

    // ---------- stats strip ----------
    &__stats {
        display: grid;
        grid-template-columns: repeat(7, minmax(0, 1fr));
        margin-top: 14px;
        border: 1px solid var(--gsf-border);
        border-radius: 14px;
        background: #fff;
        overflow: hidden;
        box-shadow: 0 1px 2px rgba(24, 34, 48, 0.04);
    }

    &__stat {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 14px 18px;
        min-width: 0;

        & + & { border-left: 1px solid var(--gsf-border); }
    }

    &__stat-value {
        font-size: 18px;
        font-weight: 800;
        letter-spacing: -0.01em;
        font-variant-numeric: tabular-nums;
        color: var(--gsf-fg);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__stat-title {
        font-size: 12px;
        font-weight: 600;
        color: var(--gsf-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__stat--accent &__stat-value { color: var(--gsf-primary); }

    // Keep the filter bar on one row: tighten spacing and drop optional button
    // labels as space shrinks, then stack the bar on small screens.
    @media (max-width: 1500px) {
        &__bar { gap: 8px; padding: 12px 14px; }
        &__btn { padding: 0 11px; }
        &__btn.gsf__apply { min-width: 112px; }
        &__search { max-width: 360px; }
        &__date { max-width: 240px; }
        &__btn-text--opt, &__rows-unit, &__switch-label { display: none; }
        &__switch { padding: 0 8px; }
    }

    @media (max-width: 1150px) {
        &__bar { gap: 5px; padding: 10px 11px; }
        &__btn { padding: 0 8px; }
        &__btn.gsf__apply { min-width: 100px; }
        &__switch { padding: 0 6px; }
    }

    @media (max-width: 1080px) {
        &__bar { flex-wrap: wrap; padding: 12px; }
        &__search { flex-basis: 100%; min-width: 0; max-width: none; }
        &__date { max-width: none; flex: 1 1 auto; }
        &__actions { width: 100%; flex-wrap: wrap; margin-left: 0; gap: 8px; }
        &__apply-group { flex: 1; }
        &__btn.gsf__apply { width: 100%; }
    }

    // ---------- responsive ----------
    @media (max-width: 1200px) {
        &__stats { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        &__stat:nth-child(n + 5) { border-top: 1px solid var(--gsf-border); }
        &__stat:nth-child(5) { border-left: 0; }
    }

    @media (max-width: 720px) {
        &__bar { flex-wrap: wrap; padding: 12px; }
        &__search { flex-basis: 100%; min-width: 0; max-width: none; }
        &__date { max-width: none; flex: 1 1 auto; }
        &__spacer { display: none; }
        &__actions { width: 100%; flex-wrap: wrap; margin-left: 0; gap: 8px; }
        &__apply-group { flex: 1; }
        &__apply { width: 100%; }
        &__feedback { margin-left: 4px; margin-right: 4px; }
        &__btn--primary { flex: 1 1 100%; margin-top: 4px; }
        &__stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        &__stat:nth-child(n + 3) { border-top: 1px solid var(--gsf-border); }
        &__stat:nth-child(odd) { border-left: 0; }
        &__stat:nth-child(even) { border-left: 1px solid var(--gsf-border); }
    }
}

@keyframes gsf-apply-nudge {
    0%, 24%, 100% { transform: translateY(0); }
    10% { transform: translateY(-3px); }
    17% { transform: translateY(1px); }
}

.gsf-fade-enter-active, .gsf-fade-leave-active { transition: opacity 140ms ease; }
.gsf-fade-enter, .gsf-fade-leave-to { opacity: 0; }

// Kept for other pages (customers, leads) that still use `animated bounce`
.animated {
    animation-duration: 1s;
    animation-fill-mode: both;
}

@keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40%, 60% { transform: translateY(-5px); }
}

.bounce { animation-name: bounce; }

@media (prefers-reduced-motion: reduce) {
    .gsf *, .gsf *::before, .gsf *::after { transition: none !important; animation: none !important; }
}
</style>
